import { DefaultComponentProps } from "@/interfaces/common";
import clsx from "clsx";
import { isString } from "lodash-es";

export type Color =
  | "primary"
  | "secondary"
  | "tertiary"
  | "light"
  | "neutral-success"
  | "dark"
  | "error"
  | "light-gray"
  | "info"
  | "warning"
  | "alert"
  | "notice";

interface Props<T extends string> extends DefaultComponentProps {
  color:
    | Color
    | {
        value: string;
        schema: Record<T, Color>;
      };
}

export function Tag<T extends string>({ children, className, color }: Readonly<Props<T>>): JSX.Element {
  const colorValue = isString(color) ? color : color.schema[color.value as T];

  const classes = clsx({
    "bg-badge-primary": colorValue === "primary",
    "bg-badge-secondary text-warning": colorValue === "secondary",
    "bg-badge-tertiary text-tertiary": colorValue === "tertiary",
    "rounded-xs bg-background-secondary font-normal": colorValue === "light",
    "bg-badge-success text-success": colorValue === "neutral-success",
    "bg-background-quaternary text-body": colorValue === "dark",
    "bg-badge-error text-error": colorValue === "error",
    "bg-background-secondary text-gray": colorValue === "light-gray",
    "bg-info-10 text-info": colorValue === "info",
    "bg-warning-10 text-warning": colorValue === "warning",
    "bg-alert-10 text-alert": colorValue === "alert",
    "bg-notice-10 text-[#C7880E]": colorValue === "notice",
  });

  return (
    <span
      className={clsx(
        "inline-block w-fit min-w-fit overflow-hidden whitespace-nowrap rounded-[1.5rem] px-2 py-1 font-medium",
        classes,
        className,
      )}
    >
      {children}
    </span>
  );
}
